import { useState } from 'react';
import {
  MapPinIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

// Route colors matching VoterMapComponent
const ROUTE_COLORS = [
  '#DC2626', // red
  '#2563EB', // blue
  '#16A34A', // green
  '#CA8A04', // yellow
  '#9333EA', // purple
  '#EA580C', // orange
  '#0D9488', // teal
  '#DB2777', // pink
  '#4F46E5', // indigo
  '#059669', // emerald
];

function formatDuration(minutes) {
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatDistance(miles) {
  if (miles === undefined || miles === null || isNaN(miles)) {
    return '0 mi';
  }
  return `${miles.toFixed(1)} mi`;
}

function RouteCard({ route, index, isSelected, onSelect, color }) {
  const [expanded, setExpanded] = useState(false);

  // API returns 'voters' array, not 'stops'
  const stops = route.voters || route.stops || [];
  const stopCount = route.door_count || stops.length || 0;
  // API returns estimated_time_minutes and total_distance_miles
  const totalTimeMinutes = route.estimated_time_minutes || route.total_time_minutes || 0;
  const totalDistanceMiles = route.total_distance_miles || (route.total_distance_meters ? route.total_distance_meters / 1609.34 : 0);

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
      }`}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
        onClick={() => onSelect(isSelected ? null : route.canvasser_id)}
      >
        {/* Color indicator */}
        <div
          className="w-4 h-4 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />

        {/* Route info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">
              Canvasser {index + 1}
            </span>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {stopCount} stops
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span className="flex items-center gap-1">
              <ClockIcon className="h-3 w-3" />
              {formatDuration(totalTimeMinutes)}
            </span>
            <span className="flex items-center gap-1">
              <MapPinIcon className="h-3 w-3" />
              {formatDistance(totalDistanceMiles)}
            </span>
          </div>
        </div>

        {/* Expand button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          {expanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Expanded stops list */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-3 max-h-48 overflow-y-auto">
          <div className="space-y-2">
            {stops.map((stop, stopIndex) => (
              <div
                key={stop.id || stop.voter_id || stopIndex}
                className="flex items-start gap-2 text-xs"
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-medium"
                  style={{ backgroundColor: color }}
                >
                  {stop.order || stopIndex + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {stop.name || `Voter ${(stop.id || stop.voter_id)?.slice(0, 8)}`}
                  </div>
                  <div className="text-gray-500 truncate">
                    {stop.address || `${stop.lat?.toFixed(4)}, ${stop.lng?.toFixed(4)}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RouteVisualization({
  routes = [],
  selectedRoute,
  onRouteSelect,
  onClearRoutes,
  onExport
}) {
  const [exportFormat, setExportFormat] = useState(null);

  // Calculate totals - handle both API field names
  const totalStops = routes.reduce((sum, r) => sum + (r.door_count || r.voters?.length || r.stops?.length || 0), 0);
  const totalTime = routes.reduce((sum, r) => sum + (r.estimated_time_minutes || r.total_time_minutes || 0), 0);
  const totalDistance = routes.reduce((sum, r) => {
    if (r.total_distance_miles !== undefined) return sum + r.total_distance_miles;
    if (r.total_distance_meters !== undefined) return sum + (r.total_distance_meters / 1609.34);
    return sum;
  }, 0);

  const handleExport = (format) => {
    setExportFormat(format);
    if (onExport) {
      onExport(format, routes);
    }

    try {
      let content, filename, mimeType;

      if (format === 'csv') {
        // Generate CSV
        const headers = ['Route', 'Order', 'Name', 'Address', 'Phone', 'Party', 'Age', 'Lat', 'Lng'];
        const rows = [headers.join(',')];

        routes.forEach((route, routeIndex) => {
          const stops = route.voters || route.stops || [];
          stops.forEach((stop, stopIndex) => {
            const row = [
              `Canvasser ${routeIndex + 1}`,
              stop.order || stopIndex + 1,
              `"${(stop.name || '').replace(/"/g, '""')}"`,
              `"${(stop.address || '').replace(/"/g, '""')}"`,
              stop.phone || '',
              stop.party || '',
              stop.age || '',
              stop.lat || '',
              stop.lng || ''
            ];
            rows.push(row.join(','));
          });
        });

        content = rows.join('\n');
        filename = `canvassing-routes-${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      } else if (format === 'gpx') {
        // Generate GPX
        const gpxContent = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="ElectionFabric" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>Canvassing Routes</name>
    <time>${new Date().toISOString()}</time>
  </metadata>
${routes.map((route, routeIndex) => {
  const stops = route.voters || route.stops || [];
  return `  <trk>
    <name>Canvasser ${routeIndex + 1}</name>
    <trkseg>
${stops.map(stop => `      <trkpt lat="${stop.lat}" lon="${stop.lng}">
        <name>${(stop.name || '').replace(/[<>&'"]/g, '')}</name>
        <desc>${(stop.address || '').replace(/[<>&'"]/g, '')}</desc>
      </trkpt>`).join('\n')}
    </trkseg>
  </trk>`;
}).join('\n')}
</gpx>`;

        content = gpxContent;
        filename = `canvassing-routes-${new Date().toISOString().split('T')[0]}.gpx`;
        mimeType = 'application/gpx+xml';
      } else if (format === 'pdf') {
        // For PDF, generate a simple HTML that can be printed
        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Canvassing Routes</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
    h1 { font-size: 18px; margin-bottom: 10px; }
    h2 { font-size: 14px; margin-top: 20px; margin-bottom: 5px; border-bottom: 1px solid #ccc; }
    .summary { background: #f5f5f5; padding: 10px; margin-bottom: 20px; }
    .stop { padding: 5px 0; border-bottom: 1px solid #eee; }
    .stop-num { display: inline-block; width: 25px; font-weight: bold; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h1>Canvassing Routes - ${new Date().toLocaleDateString()}</h1>
  <div class="summary">
    <strong>Total:</strong> ${totalStops} stops | ${formatDuration(totalTime)} | ${formatDistance(totalDistance)}
  </div>
${routes.map((route, routeIndex) => {
  const stops = route.voters || route.stops || [];
  const time = route.estimated_time_minutes || route.total_time_minutes || 0;
  const dist = route.total_distance_miles || (route.total_distance_meters ? route.total_distance_meters / 1609.34 : 0);
  return `
  <h2>Canvasser ${routeIndex + 1} (${stops.length} stops, ${formatDuration(time)}, ${formatDistance(dist)})</h2>
  <div>
${stops.map((stop, i) => `    <div class="stop">
      <span class="stop-num">${stop.order || i + 1}.</span>
      <strong>${stop.name || 'Unknown'}</strong> - ${stop.address || ''} ${stop.phone ? `| ${stop.phone}` : ''} ${stop.party ? `| ${stop.party}` : ''}
    </div>`).join('\n')}
  </div>`;
}).join('\n')}
</body>
</html>`;

        // Open in new window for printing
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          printWindow.print();
        }
        setTimeout(() => setExportFormat(null), 500);
        return;
      }

      // Download file for CSV and GPX
      if (content) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
    }

    setTimeout(() => setExportFormat(null), 500);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <MapPinIcon className="h-4 w-4" />
          Optimized Routes ({routes.length})
        </h3>
        <button
          onClick={onClearRoutes}
          className="p-1 text-gray-400 hover:text-gray-600"
          title="Clear routes"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 border-b border-gray-100">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">{totalStops}</div>
          <div className="text-xs text-gray-500">Total Stops</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">{formatDuration(totalTime)}</div>
          <div className="text-xs text-gray-500">Total Time</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">{formatDistance(totalDistance)}</div>
          <div className="text-xs text-gray-500">Total Distance</div>
        </div>
      </div>

      {/* Route list */}
      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
        {routes.map((route, index) => (
          <RouteCard
            key={route.canvasser_id || index}
            route={route}
            index={index}
            isSelected={selectedRoute === route.canvasser_id}
            onSelect={onRouteSelect}
            color={ROUTE_COLORS[index % ROUTE_COLORS.length]}
          />
        ))}
      </div>

      {/* Export options */}
      <div className="p-3 border-t border-gray-100">
        <div className="text-xs font-medium text-gray-600 mb-2">Export Routes</div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('pdf')}
            disabled={exportFormat === 'pdf'}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            <DocumentTextIcon className="h-3 w-3" />
            PDF
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={exportFormat === 'csv'}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-3 w-3" />
            CSV
          </button>
          <button
            onClick={() => handleExport('gpx')}
            disabled={exportFormat === 'gpx'}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-3 w-3" />
            GPX
          </button>
        </div>
      </div>
    </div>
  );
}
